import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
// material
import { experimentalStyled as styled, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
//
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import useApi from 'src/hooks/useApi';
import { Alert, Box } from '@material-ui/core';
import OfflineIndicator from '../../components/OfflineIndicator';
import { useHistory } from 'react-router-dom';

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden'
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

// ----------------------------------------------------------------------

DashboardLayout.propTypes = {
  children: PropTypes.node
};

export default function DashboardLayout({ children }) {
  const [open, setOpen] = useState(false);
  const api = useApi();
  const history = useHistory();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const lockoutMode = api.site?.config?.lockoutMode;
  const maintenanceOn = Boolean(api.site?.config?.maintenance);
  const currentVersion = api.site?.config?.firmware ?? api.site?.firmwareVersion;
  const userEmail = api.user?.email;
  const acknowledgedEmails = api.site?.acknowledgedVersions?.[currentVersion] || [];
  const showVersionBanner = currentVersion && userEmail && !acknowledgedEmails.includes(userEmail);

  // Measure the banner stack and offset the navbar/sidebar/content by it, so
  // fixed banners (lockout/maintenance/version) never cover the menus.
  const bannerRef = useRef(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  useEffect(() => {
    const measure = () => {
      setBannerHeight(bannerRef.current ? bannerRef.current.getBoundingClientRect().height : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [showVersionBanner, lockoutMode, maintenanceOn]);

  const handleAcknowledgeVersion = () => {
    api.acknowledgeVersion({ version: currentVersion });
  };

  const handleBannerClick = () => {
    handleAcknowledgeVersion();
    history.push('/app/docs/features');
  };

  const topPadding = (isDesktop ? APP_BAR_DESKTOP : APP_BAR_MOBILE) + 24 + bannerHeight;

  return (
    <RootStyle>
      <OfflineIndicator />

      <Box ref={bannerRef} sx={{ width: '100%', position: 'fixed', top: 0, zIndex: 1201, display: 'flex', flexDirection: 'column' }}>
        {showVersionBanner && (
          <Alert 
            severity="info" 
            variant="filled" 
            onClose={handleAcknowledgeVersion}
            style={{ cursor: 'pointer', backgroundColor: '#2E9B6B', color: '#fff' }}
            onClick={(e) => {
              if (e.target.closest('button')) return;
              handleBannerClick();
            }}
          >
            A new firmware version ({currentVersion}) is active! Click here to see the new features.
          </Alert>
        )}
        {lockoutMode === 'ON' && (
          <Alert severity="warning" variant="filled">
            SYSTEM WIDE LOCKOUT ACTIVE: ALL GLYCOL OUTPUTS FORCED ON
          </Alert>
        )}
        {lockoutMode === 'OFF' && (
          <Alert severity="error" variant="filled">
            SYSTEM WIDE LOCKOUT ACTIVE: ALL GLYCOL OUTPUTS FORCED OFF
          </Alert>
        )}
        {api.site?.config?.maintenance && (
          <Alert 
            severity="warning" 
            variant="filled"
            style={{ backgroundColor: '#b45309', color: '#fff', fontWeight: 700, letterSpacing: '0.05em' }}
          >
            ⚠️ MAINTENANCE MODE ACTIVE — Temperature control and automation are paused. Remember to disable maintenance mode when complete.
          </Alert>
        )}
      </Box>
      <DashboardNavbar onOpenSidebar={() => setOpen(true)} offsetTop={bannerHeight} />
      <DashboardSidebar
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
        offsetTop={bannerHeight}
      />
      <MainStyle style={{ paddingTop: topPadding }}>{children}</MainStyle>
    </RootStyle>
  );
}
